/**
 * Asteri product set PDP — shade swatches + ATC line-item properties.
 * First available shade is preselected; ATC only disables for sold-out.
 */
(() => {
  const texts = {
    en: {
      soldOut: 'Sold out',
      addToCart: 'Add to bag',
    },
    ar: {
      soldOut: 'نفدت الكمية',
      addToCart: 'أضيفي إلى السلة',
    },
  };

  const locale = (window.Shopify && window.Shopify.locale) || 'en';
  const copy = texts[locale] || texts.en;

  function uniqueByVariant(items) {
    const counts = {};
    items.forEach((item) => {
      const id = String(item.variantid);
      counts[id] = (counts[id] || 0) + 1;
    });

    return Object.keys(counts).map((id) => {
      const base = items.find((item) => String(item.variantid) === id);
      return { ...base, quantity: counts[id] };
    });
  }

  function setButtonLabel(button, text) {
    const label = button.querySelector('.add-to-cart-text__content');
    if (!label) return;

    const nested = label.querySelector('span span') || label.querySelector('span') || label;
    nested.textContent = text;
  }

  function getCheckedValues(root) {
    return [...root.querySelectorAll('.product-set__value:checked')];
  }

  function parseValue(input) {
    try {
      return JSON.parse(input.value);
    } catch {
      return null;
    }
  }

  function findProductForm(root) {
    return (
      root.closest('.product-information, .shopify-section')?.querySelector(
        'form[data-type="add-to-cart-form"]'
      ) || document.querySelector('product-form-component form[data-type="add-to-cart-form"]')
    );
  }

  function updateAddToCartState(root) {
    const checked = getCheckedValues(root);
    let soldOut = false;

    checked.forEach((input) => {
      if (input.disabled || input.dataset.soldOut === 'true') {
        soldOut = true;
      }
    });

    const form = findProductForm(root);
    const buttons = form
      ? form.querySelectorAll('button[type="submit"][name="add"]')
      : document.querySelectorAll('product-form-component button[type="submit"][name="add"]');

    buttons.forEach((button) => {
      if (soldOut) {
        button.disabled = true;
        setButtonLabel(button, copy.soldOut);
      } else {
        button.disabled = false;
        setButtonLabel(button, copy.addToCart);
      }
    });
  }

  function updateShadeLabel(item, input) {
    const labelEl = item.querySelector('[data-set-shade-value]');
    if (!labelEl || !input) return;
    labelEl.textContent = input.dataset.shadeLabel || '';
  }

  function updateItemImage(item, input) {
    const img = item.querySelector('[data-set-item-media] img');
    const src = input?.dataset.optionImage;
    if (!img || !src) return;
    img.src = src;
    img.srcset = '';
  }

  function injectSetProperties(form, root) {
    if (!form || !root) return;

    const checked = getCheckedValues(root).map(parseValue).filter(Boolean);

    const quantities = uniqueByVariant(checked).map((item) => ({
      id: String(item.variantid || ''),
      productid: String(item.productid || ''),
      quantity: item.quantity || 1,
      title: item.title || '',
      option: item.option || '',
      image: item.image || '',
      ...(item.price !== undefined && item.price !== null && item.price !== ''
        ? { price: Number(item.price) }
        : {}),
    })).filter((item) => item.id);

    const setImage = root.querySelector('.product-set__image')?.value || '';
    const variantIds = quantities.map((item) => item.id).join(',');

    form.querySelectorAll('.product-set-prop').forEach((el) => el.remove());

    const makeHidden = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.className = 'product-set-prop';
      form.appendChild(input);
    };

    makeHidden('properties[_setproduct]', 'true');
    makeHidden('properties[_quantitiesarray]', JSON.stringify(quantities));
    makeHidden('properties[_setvariantids]', variantIds);
    makeHidden('properties[_setimage]', setImage);
  }

  function syncSetProperties(root) {
    const form = findProductForm(root);
    if (!form) return;
    injectSetProperties(form, root);
  }

  function bindItem(item, root) {
    item.querySelectorAll('.product-set__value').forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        updateShadeLabel(item, input);
        updateItemImage(item, input);
        updateAddToCartState(root);
        syncSetProperties(root);
      });
    });

    const checked = item.querySelector('.product-set__value:checked');
    if (checked) {
      updateShadeLabel(item, checked);
      updateItemImage(item, checked);
    }
  }

  function bindForm(root) {
    const form = findProductForm(root);
    if (!form) return;

    // Keep props fresh before theme capture-phase submit builds FormData.
    const ensureProps = () => injectSetProperties(form, root);

    if (form.dataset.productSetBound !== 'true') {
      form.dataset.productSetBound = 'true';
      form.addEventListener('submit', ensureProps, true);
    }

    // Always sync current selections so FormData is correct even if
    // product-form handleSubmit runs earlier in the capture chain.
    ensureProps();
  }

  function bindGlobalInject() {
    if (document.documentElement.dataset.productSetInjectBound === 'true') return;
    document.documentElement.dataset.productSetInjectBound = 'true';

    const syncForEventTarget = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('button[type="submit"][name="add"]');
      if (!button) return;

      const form = button.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;

      document.querySelectorAll('[data-product-set]').forEach((root) => {
        const setForm = findProductForm(root);
        if (setForm === form) injectSetProperties(form, root);
      });
    };

    // pointerdown runs before theme document-capture submit/click handlers.
    document.addEventListener('pointerdown', syncForEventTarget, true);
    document.addEventListener('click', syncForEventTarget, true);
  }

  function init() {
    const roots = document.querySelectorAll('[data-product-set]');
    if (!roots.length) return;

    bindGlobalInject();

    roots.forEach((root) => {
      root.querySelectorAll('[data-set-item]').forEach((item) => bindItem(item, root));
      bindForm(root);
      updateAddToCartState(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
